import { useEffect, useMemo, useRef, useState } from "react";
import { useBlocker, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PACE_BANDS, type BookSearchResult, type Comment, type CreateClubInput, type PickPolicy, type Visibility } from "@pagemates/shared";
import { VIS_OPTIONS, VisibilityChoice } from "./club/ClubSettingsPage";
import { api, describeError } from "../lib/api";
import { GenreChips } from "../components/ClubHome";
import { BookSearch } from "../components/BookSearch";
import { Cover } from "../components/BookBits";
import { PacePicker } from "../components/PacePicker";
import { CloseIcon, PencilIcon } from "../components/Icons";
import { Spinner } from "../components/ui";
import { Bookplate, type PlateFacts } from "../components/Bookplate";
import { Book3D } from "../components/Book3D";
import { SealedRow, StaticMessage } from "../components/Discussion";
import { posLabel } from "../lib/format";
import { useMe } from "../lib/queries";
import { newClub } from "@pagemates/shared/copy";

const txt = newClub.page;

/*
 * Starting a club (PLAN.md §Starting a club): a few short steps, each with a
 * default already chosen, so Next straight through makes a working club.
 * The step is in the URL, so the browser's back (and a phone's back
 * gesture) goes back one step; answers sit in sessionStorage so a refresh
 * keeps them. The club is made on the last step before the invite, with a
 * one-time key so a double tap can't make two.
 *
 * Steps are a list with an on/off flag. "find" (who can find this club) is
 * built but off: every club is private, invite only, until public clubs
 * exist. Turning it on is a matter of flipping its flag and giving it a body.
 */
type StepKey = "name" | "read" | "together" | "picking" | "first" | "find" | "invite";
const STEPS: { key: StepKey; enabled: boolean }[] = [
  { key: "name", enabled: true },
  { key: "read", enabled: true },
  { key: "together", enabled: true },
  { key: "picking", enabled: true },
  { key: "first", enabled: true }, // optional: the club's first book, started on creation
  { key: "find", enabled: true },
  { key: "invite", enabled: true },
];
const FLOW = STEPS.filter((s) => s.enabled).map((s) => s.key);
const INVITE = FLOW.indexOf("invite");

interface Answers {
  name: string;
  description: string;
  slug: string;
  slugEdited: boolean;
  genres: string[];
  rules: string;
  usualPace: number | null;
  spoilerGating: boolean;
  pickPolicy: PickPolicy;
  onDeckPolicy: "everyone" | "admins";
  firstBook: BookSearchResult | null;
  visibility: Visibility;
  createKey: string;
  created: { id: string; slug: string; name: string } | null;
}
const STORE = "pm:new-club";
const fresh = (): Answers => ({
  name: "", description: "", slug: "", slugEdited: false, genres: [], rules: "",
  usualPace: PACE_BANDS[1].preset, spoilerGating: true, pickPolicy: "admins", onDeckPolicy: "everyone", firstBook: null, visibility: "private",
  createKey: crypto.randomUUID(), created: null,
});
const load = (): Answers => {
  try { const raw = sessionStorage.getItem(STORE); if (raw) return { ...fresh(), ...JSON.parse(raw) as Partial<Answers> }; } catch { /* private mode */ }
  return fresh();
};
const keep = (a: Answers) => { try { sessionStorage.setItem(STORE, JSON.stringify(a)); } catch { /* private mode */ } };
const forget = () => { try { sessionStorage.removeItem(STORE); } catch { /* private mode */ } };

/** The same rules as the server's: lowercase letters, digits and single hyphens, 3–40 long. */
function slugify(name: string): string {
  const s = name.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/['’]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40).replace(/-+$/g, "");
  return s.length >= 3 ? s : s ? `${s}club`.slice(0, 40) : "";
}
const host = () => window.location.host;

export function NewClub() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { step: rawStep = 0 } = useSearch({ from: "/authed/clubs/new" });
  const me = useMe();
  const [a, setA] = useState<Answers>(load);
  const [nameErr, setNameErr] = useState(false);
  const [descErr, setDescErr] = useState(false);
  const set = (patch: Partial<Answers>) => { if (patch.name !== undefined) setNameErr(false); if (patch.description !== undefined || patch.visibility !== undefined) setDescErr(false); setA((p) => { const n = { ...p, ...patch }; keep(n); return n; }); };

  // Where you may be: nowhere past step 1 without a name, and only the invite once the club exists.
  const step = a.created ? INVITE : Math.min(Math.max(0, rawStep), a.name.trim() ? INVITE - 1 : 0);
  useEffect(() => { if (step !== rawStep) void navigate({ to: "/clubs/new", search: { step }, replace: true }); }, [step, rawStep, navigate]);
  const go = (n: number) => void navigate({ to: "/clubs/new", search: { step: n } });

  // Leaving partway asks once; nothing to lose (or a club already made) leaves quietly.
  const started = !a.created && (a.name.trim() !== "" || a.description.trim() !== "" || a.genres.length > 0 || a.rules.trim() !== "");
  useBlocker({
    shouldBlockFn: ({ next }) => {
      if (next.pathname === "/clubs/new" || !started) return false;
      if (window.confirm(txt.discardThisClub)) { forget(); return false; }
      return true;
    },
    enableBeforeUnload: false,
  });

  const owned = me.data?.clubs.filter((c) => c.role === "owner").length ?? 0;
  const create = useMutation({
    mutationFn: async () => {
      const input: CreateClubInput = {
        name: a.name.trim(), description: a.description.trim() || undefined, slug: a.slug || undefined,
        genres: a.genres, rules: a.rules.trim() || undefined, createKey: a.createKey, visibility: a.visibility,
        settings: { usualPace: a.usualPace, spoilerGating: a.spoilerGating, pickPolicy: a.pickPolicy, onDeckPolicy: a.onDeckPolicy },
      };
      const club = await api.createClub(input);
      // The first book, if one was chosen: on the shelf and started, so the club opens on it.
      // A retry finds it already current, which counts as success.
      if (a.firstBook) {
        try { await api.placeBook(club.id, { hardcoverId: a.firstBook.hardcoverId, status: "current" }); } // started directly: never a suggestion, idempotent on retry
        catch { /* the club exists; the book can be picked from its Reading tab */ }
      }
      return club;
    },
    onSuccess: async (club) => {
      set({ created: { id: club.id, slug: club.slug, name: club.name } });
      await qc.invalidateQueries({ queryKey: ["me"] });
      void navigate({ to: "/clubs/new", search: { step: INVITE }, replace: true });
    },
  });

  if (me.isPending) return <main className="page"><Spinner /></main>;
  if (!a.created && owned >= 5) {
    return (
      <main className="page newclub">
        <div className="ob card">
          <h1 className="ob-t">{txt.youAlreadyRunFive}</h1>
          <p className="ob-a">{txt.thatSTheMost}</p>
          <div className="ob-foot"><span /><button type="button" className="btn primary" onClick={() => void navigate({ to: "/clubs", search: { all: true } })}>{txt.backToYourClubs}</button></div>
        </div>
      </main>
    );
  }

  const key = FLOW[step]!;
  const last = step === INVITE - 1;
  // Next is never disabled: with no name it says so, and puts you in the field.
  const next = () => {
    if (key === "name" && !a.name.trim()) { setNameErr(true); document.getElementById("nc-name")?.focus(); return; }
    if (key === "find" && a.visibility !== "private" && !a.description.trim()) { setDescErr(true); document.getElementById("nc-find-desc")?.focus(); return; }
    if (last) create.mutate(); else go(step + 1);
  };
  const finish = async () => {
    const slug = a.created!.slug;
    forget();
    await qc.invalidateQueries({ queryKey: ["me"] });
    void navigate({ to: "/clubs/$clubId", params: { clubId: slug }, replace: true });
  };
  const owner = (me.data?.displayName ?? me.data?.name ?? txt.you).split(/\s+/)[0]!;
  // The bookplate fills in as you answer: each step adds its line once you've reached it.
  const reached = (k: StepKey) => step >= FLOW.indexOf(k);
  const facts: PlateFacts = {
    name: a.name, description: a.description,
    genres: reached("read") ? a.genres : [],
    pace: reached("together") && a.usualPace ? txt.aboutPagesADay(a.usualPace) : null,
    pick: reached("picking") ? PICK_SHORT[a.pickPolicy] : null,
    first: a.firstBook ? a.firstBook.title : null,
    find: reached("find") && a.visibility !== "private" ? VIS_OPTIONS.find((o) => o.key === a.visibility)!.label : null,
  };
  const latest = { name: a.description.trim() || null, read: a.genres.join(" · ") || null, together: facts.pace, picking: facts.pick, first: a.firstBook ? txt.firstUp(a.firstBook.title) : null, find: VIS_OPTIONS.find((o) => o.key === a.visibility)!.label, invite: null }[key];

  return (
    <main className="page newclub">
      <div className="ob card">
        <div className="ob-top">
          {key !== "invite"
            ? <button type="button" className="pencil" aria-label={txt.close} onClick={() => void navigate({ to: "/clubs", search: { all: true } })}><CloseIcon width={20} height={20} /></button>
            : <span style={{ width: 32 }} />}
          <span className="muted small">{a.created?.name ?? txt.newClub}</span>
          <span style={{ width: 32 }} />
        </div>
        <form className="ob-form" noValidate onSubmit={(e) => { e.preventDefault(); if (key === "invite") void finish(); else next(); }}>
          <div className="ob-scroll">
            <div className="ob-dots" aria-label={txt.stepOf(step + 1, FLOW.length)}>{FLOW.map((k, i) => <i key={k} className={i === step ? "on" : i < step ? "done" : ""} />)}</div>
            <div className={`ob-grid${key === "invite" ? " solo" : ""}`}>
              <div className="ob-left">
                {key !== "invite" && <div className="ob-compact"><Bookplate facts={facts} variant="compact" latest={latest} /></div>}
                {key === "name" && <NameStep a={a} set={set} err={nameErr} />}
                {key === "read" && <ReadStep a={a} set={set} />}
                {key === "together" && <TogetherStep a={a} set={set} />}
                {key === "picking" && <PickingStep a={a} set={set} owner={owner} />}
                {key === "find" && <FindStep a={a} set={set} err={descErr} />}
                {key === "first" && <FirstBookStep a={a} set={set} onSkip={() => { set({ firstBook: null }); go(step + 1); }} />}
                {key === "invite" && a.created && <InviteStep club={a.created} facts={facts} first={a.firstBook} />}
                {create.isError && <p className="error small">{describeError(create.error)}</p>}
              </div>
              {key !== "invite" && <aside className="ob-plate"><Bookplate facts={facts} /></aside>}
            </div>
          </div>
          <div className="ob-foot">
            {step > 0 && key !== "invite" ? <button type="button" className="btn ghost" onClick={() => window.history.back()}>{txt.back}</button> : <span />}
            <button type="submit" className="btn primary" disabled={create.isPending}>
              {key === "invite" ? txt.goToYourClub : last ? (create.isPending ? txt.creating : txt.createClub) : txt.next}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}

const PICK_SHORT: Record<PickPolicy, string> = { admins: txt.pickedByAdmins, vote: txt.pickedByVote, turns: txt.pickedInTurns };

type StepProps = { a: Answers; set: (p: Partial<Answers>) => void };

function Head({ title, about }: { title: string; about: string }) {
  return <><h1 className="ob-t">{title}</h1><p className="ob-a">{about}</p></>;
}

function NameStep({ a, set, err }: StepProps & { err: boolean }) {
  const [editing, setEditing] = useState(false);
  const slug = a.slugEdited ? a.slug : slugify(a.name);
  useEffect(() => { if (!a.slugEdited && a.slug !== slug) set({ slug }); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  // Check the link once typing settles.
  const [settled, setSettled] = useState(slug);
  useEffect(() => { const t = setTimeout(() => setSettled(slug), 300); return () => clearTimeout(t); }, [slug]);
  const check = useQuery({ queryKey: ["club-link", settled], queryFn: () => api.clubLink(settled), enabled: settled.length > 0, staleTime: 10_000 });
  const c = check.data && check.data.slug === settled ? check.data : null;
  return (
    <>
      <Head title={txt.nameYourClub} about={txt.aClubReadsOne} />
      <div className="field"><label htmlFor="nc-name">{txt.clubName}</label>
        <input id="nc-name" className={`input${err ? " err" : ""}`} value={a.name} maxLength={60} placeholder={txt.eGTuesdayPages} autoFocus autoComplete="off"
          aria-invalid={err} aria-describedby={err ? "nc-name-err" : undefined} onChange={(e) => set({ name: e.target.value })} />
        {err && <span id="nc-name-err" className="small warn" role="alert">{txt.giveYourClubA}</span>}</div>
      {slug && (editing ? (
        <div className="field"><label htmlFor="nc-slug">{txt.link}</label>
          <div className="row" style={{ gap: 6 }}>
            <span className="muted small">{txt.clubs(host())}</span>
            <input id="nc-slug" className="input" style={{ maxWidth: 220 }} value={a.slug} maxLength={40} autoFocus
              onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"), slugEdited: true })}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setEditing(false); } }} />
            <button type="button" className="btn ghost sm" onClick={() => setEditing(false)}>{txt.done}</button>
          </div>
          {a.slugEdited && <button type="button" className="linkish small" style={{ alignSelf: "flex-start" }} onClick={() => { set({ slugEdited: false, slug: slugify(a.name) }); setEditing(false); }}>{txt.useOneFromThe}</button>}
        </div>
      ) : (
        <div className="ob-link">
          <span className="muted small">{txt.clubs(host())}</span><b>{slug}</b>
          <button type="button" className="pencil" aria-label={txt.changeTheLink} onClick={() => setEditing(true)}><PencilIcon width={15} height={15} /></button>
        </div>
      ))}
      {slug && c && (c.available ? <span className="ok small ob-check">{txt.available}</span> : (
        <span className="small ob-check warn">
          {c.valid ? txt.anotherClubHasThat : txt.linksNeed3To}
          {c.suggestion && <> <button type="button" className="linkish small" onClick={() => set({ slug: c.suggestion!, slugEdited: true })}>{txt.use(c.suggestion)}</button></>}
        </span>
      ))}
      <div className="field"><label htmlFor="nc-desc">{txt.description}</label>
        <input id="nc-desc" className="input" value={a.description} maxLength={300} placeholder={txt.fantasyMostlySnacksMandatory} onChange={(e) => set({ description: e.target.value })} />
        <span className="muted small">{txt.oneLineShownTo}</span></div>
    </>
  );
}

function ReadStep({ a, set }: StepProps) {
  return (
    <>
      <Head title={txt.whatDoYouRead} about={txt.pickUpToFive} />
      <GenreChips value={a.genres} onChange={(genres) => set({ genres })} />
      <div className="field"><label htmlFor="nc-rules">{txt.howThisClubWorks}<span className="muted">{txt.optional}</span></label>
        <GrowingTextarea id="nc-rules" value={a.rules} maxLength={2000} placeholder={txt.howOftenYouMeet} onChange={(rules) => set({ rules })} /></div>
    </>
  );
}

function TogetherStep({ a, set }: StepProps) {
  return (
    <>
      <Head title={txt.howYouReadTogether} about={txt.everyoneReadsAtTheir} />
      <div className="ob-hero">
        <div className="ob-hero-row">
          <div>
            <div className="ob-hero-t">{txt.hideSpoilers}</div>
            <div className="d">{txt.eachCommentIsPinned}</div>
          </div>
          <input id="nc-spoilers" className="toggle" type="checkbox" checked={a.spoilerGating} aria-label={txt.hideSpoilers} onChange={(e) => set({ spoilerGating: e.target.checked })} />
        </div>
        <SpoilerDemo on={a.spoilerGating} />
      </div>
      <div className="switch stacked">
        <div><div className="k">{txt.usualPace}</div><div className="d">{txt.roughlyHowManyPages}</div></div>
        <PacePicker id="nc-pace" value={a.usualPace} onChange={(usualPace) => set({ usualPace })} />
        {a.usualPace && <div className="muted small">{txt.a300PageBook(spanFor(300, a.usualPace))}</div>}
      </div>
    </>
  );
}

/** "about 2 weeks", "about 10 days". */
const spanFor = (pages: number, pace: number) => { const days = Math.ceil(pages / pace); return days < 14 ? txt.about(days, days === 1 ? txt.day : txt.days) : txt.aboutWeeks(Math.round(days / 7)); };

/*
 * Spoiler hiding, shown: a reader at 30% of a 300-page book sees a comment
 * from p. 40, and Ana's from 62% as Talk's own sealed row. Switched off, it
 * opens. The sealed row is Talk's component; the open messages use Talk's
 * message markup (Message itself needs a live club and book).
 */
const DEMO_CTX = { pages: 300 };
const DEMO_SEALED = {
  id: "demo-ana", parentId: null, author: { id: "demo-ana", name: "Ana", image: null, former: false }, kind: "message", flagged: false, discussed: false,
  answered: false, position: 62, fromPercent: null, revealed: false, body: null, locked: true, removed: false, mine: false, isNew: false, justOpened: false,
  createdAt: new Date(0).toISOString(), editedAt: null, replies: [], replyCount: 0, lastReplyAt: null,
} satisfies Comment;

function DemoMessage({ name, position, body, fresh }: { name: string; position: number; body: string; fresh?: boolean }) {
  return <StaticMessage name={name} pos={posLabel(position, DEMO_CTX)} body={body} className={fresh ? "sp-open" : undefined} />;
}

function SpoilerDemo({ on }: { on: boolean }) {
  return (
    <div className="sp-demo" aria-label={txt.example}>
      <DemoMessage name="domdomb" position={13} body={txt.theFirstChapterHooked} />
      {on ? <SealedRow c={DEMO_SEALED} ctx={DEMO_CTX} /> : <DemoMessage name="Ana" position={62} body={txt.iDidNotSee} fresh />}
    </div>
  );
}

const pickLine = (p: PickPolicy, owner: string) => ({
  admins: txt.choosesTheNextBook(owner),
  vote: txt.everyoneVotesBetweenThe,
  turns: txt.picksFirstThenWhoever(owner),
}[p]);

function PickingStep({ a, set, owner }: StepProps & { owner: string }) {
  return (
    <>
      <Head title={txt.pickingTheNextBook} about={txt.membersSuggestBooksThis} />
      <div className="switch stacked">
        <div><div className="k">{txt.howItGetsPicked}</div></div>
        <div className="seg" role="radiogroup" aria-label={txt.howTheNextBook}>
          {([["admins", txt.adminsPick], ["vote", txt.vote], ["turns", txt.takeTurns]] as [PickPolicy, string][]).map(([k, label]) => (
            <button key={k} type="button" role="radio" aria-checked={a.pickPolicy === k} aria-pressed={a.pickPolicy === k} onClick={() => set({ pickPolicy: k })}>{label}</button>
          ))}
        </div>
        <div className="d">{pickLine(a.pickPolicy, owner)}</div>
      </div>
      <hr className="divider" />
      <div className="switch">
        <div><div className="k">{txt.whoCanSuggest}</div><div className="d">{txt.whoCanPutBooks}</div></div>
        <div className="seg" role="radiogroup" aria-label={txt.whoCanSuggest}>
          {([["everyone", txt.everyone], ["admins", txt.admins]] as const).map(([k, label]) => (
            <button key={k} type="button" role="radio" aria-checked={a.onDeckPolicy === k} aria-pressed={a.onDeckPolicy === k} onClick={() => set({ onDeckPolicy: k })}>{label}</button>
          ))}
        </div>
      </div>
    </>
  );
}

/** Who can find the club. A public mode needs the description, asked for here if it's empty. */
function FindStep({ a, set, err }: StepProps & { err: boolean }) {
  const needsDesc = a.visibility !== "private" && !a.description.trim();
  return (
    <>
      <Head title={txt.whoCanFindThis} about={txt.youCanChangeThis} />
      <VisibilityChoice value={a.visibility} onChange={(visibility) => set({ visibility })} />
      {(needsDesc || err) && (
        <div className="field"><label htmlFor="nc-find-desc">{txt.description}</label>
          <input id="nc-find-desc" className={`input${err ? " err" : ""}`} value={a.description} maxLength={300} placeholder={txt.fantasyMostlySnacksMandatory}
            aria-invalid={err} onChange={(e) => set({ description: e.target.value })} />
          <span className={`small ${err ? "warn" : "muted"}`} role={err ? "alert" : undefined}>{txt.clubsPeopleCanFind}</span></div>
      )}
    </>
  );
}

/** Optional: the book the club reads first. It goes on the shelf and starts when the club is made. */
function FirstBookStep({ a, set, onSkip }: StepProps & { onSkip: () => void }) {
  const b = a.firstBook;
  return (
    <>
      <Head title={txt.whatAreYouReading} about={txt.optionalItBecomesThe} />
      {b ? (
        <div className="first-pick">
          <Cover book={b} size="sm" />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="first-t">{b.title}</div>
            {b.authors.length > 0 && <div className="muted small">{b.authors.join(", ")}</div>}
          </div>
          <button type="button" className="btn ghost sm" onClick={() => set({ firstBook: null })}>{txt.change}</button>
        </div>
      ) : (
        <BookSearch onPick={(book) => set({ firstBook: book })} />
      )}
      {!b && <button type="button" className="linkish small" style={{ alignSelf: "flex-start" }} onClick={onSkip}>{txt.skipForNow}</button>}
    </>
  );
}

function InviteStep({ club, facts, first }: { club: { id: string; name: string }; facts: PlateFacts; first: BookSearchResult | null }) {
  const invites = useQuery({ queryKey: ["invites", club.id], queryFn: () => api.invites(club.id) });
  const make = useMutation({ mutationFn: () => api.createInvite(club.id, { singleUse: false }), onSuccess: () => void invites.refetch() });
  const inv = invites.data?.[0];
  useEffect(() => { if (invites.data && invites.data.length === 0 && !make.isPending && !make.isSuccess) make.mutate(); }, [invites.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const [copied, setCopied] = useState(false);
  const canShare = useMemo(() => typeof navigator !== "undefined" && "share" in navigator, []);
  // The finish: the closed book first (0.35s), then the cover swings open over a brass glow (0.9s),
  // then the invite. Tapping the book jumps to the end; reduced motion starts there.
  const still = useMemo(() => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches, []);
  const [opened, setOpened] = useState(still);
  const [shown, setShown] = useState(still);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (still) return;
    const swing = window.setTimeout(() => setOpened(true), 350);
    timer.current = window.setTimeout(() => setShown(true), 1150);
    return () => { window.clearTimeout(swing); window.clearTimeout(timer.current); };
  }, [still]);
  const skip = () => { window.clearTimeout(timer.current); setOpened(true); setShown(true); };
  // The first book's own cover, or a plain one with the club's name.
  const book = first ?? { hardcoverId: 0, title: club.name, authors: [], coverUrl: null, pages: 300, releaseYear: null };
  return (
    <>
      <Head title={txt.isReady(club.name)} about={txt.inviteYourFirstMembers} />
      <div className={`fin-stage${opened ? " lit" : ""}${still || (opened && shown) ? " settled" : ""}`} onClick={skip}>
        <div className="fin-glow" aria-hidden="true" />
        <div className="fin-book slot"><Book3D book={book} open={opened} inner={<Bookplate facts={facts} variant="page" />} className="fin" /></div>
      </div>
      <div className={`card ob-inv${shown ? " in" : ""}`}>
        {inv ? <code className="inline">{inv.url.replace(/^https?:\/\//, "")}</code> : <Spinner label={txt.makingALink} />}
        {inv && (
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className={`btn sm${copied ? " copied" : ""}`} aria-live="polite"
              onClick={() => { void navigator.clipboard?.writeText(inv.url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => undefined); }}>
              {copied ? <><span className="pop-check" aria-hidden="true">✓</span>{txt.copied}</> : txt.copy}
            </button>
            {canShare && <button type="button" className="btn sm" onClick={() => { void navigator.share({ title: club.name, text: txt.joinOnPagemates(club.name), url: inv.url }).catch(() => undefined); }}>{txt.share}</button>}
          </div>
        )}
        <div className="muted small">{txt.thisLinkWorksFor}</div>
        {make.isError && <p className="error small">{describeError(make.error)}</p>}
      </div>
    </>
  );
}

/** A textarea that grows with what's typed, from three lines, so nothing is ever clipped. */
function GrowingTextarea({ id, value, maxLength, placeholder, onChange }: { id: string; value: string; maxLength: number; placeholder?: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { const el = ref.current; if (!el) return; el.style.height = "auto"; el.style.height = `${el.scrollHeight + 2}px`; }, [value]);
  return <textarea ref={ref} id={id} className="input grow" rows={3} maxLength={maxLength} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />;
}
